/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { HttpReqService } from '@api/services/http-req/http-req.service';
import { ApiService } from '@api/services/api/api.service';
import { mergeMap, map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of, forkJoin, BehaviorSubject, from } from 'rxjs';
import { UserDataDTO, AuthenticateDTO, UserAttributesDTO } from '@api/models';
import { HttpResponse } from '@angular/common/http';
import { AppService } from '@services/app/app.service';
import { PushNotificationsService } from '@services/push-notifications/push-notifications.service';
import { DeviceService } from '@services/device/device.service';
import { HttpHeader } from '@core/utils';
import { BaseHttpRequest } from '@core/models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private requestTokenHeader: HttpHeader;

  /**
   * sessione utente
   */
  private authUser$ = new BehaviorSubject<UserDataDTO>(null);

  /**
   * lista di azioni che vengono eseguite dopo la logon
   */
  private afterLogonActions: Observable<void>[] = [];

  constructor(
    private appService: AppService,
    private apiService: ApiService,
    private httpReqService: HttpReqService,
    private pushNotificationsService: PushNotificationsService,
    private deviceService: DeviceService
  ) {
    this.createRequestTokenHeader();
  }

  /**
   * effettua login
   */
  login(): Observable<boolean> {
    return from(this.createAuthenticateData()).pipe(
      mergeMap(data => this.authenticate(data)),
      mergeMap(() => this.loadAuthUser())
    );
  }

  /**
   * effettua login tramite auth code
   */
  loginWithAuthCode(code: string): Observable<boolean> {
    return from(this.createAuthenticateData(code)).pipe(
      mergeMap(data => this.authenticate(data)),
      mergeMap(() => this.loadAuthUser())
    );
  }

  /**
   * avvia sessione recuperando i dati dell'utente
   */
  loadAuthUser(): Observable<boolean> {
    return this.getUserData().pipe(
      map(res => this.setAuthUser(res)),
      mergeMap(() => this.runAfterLogonActions()),
      map(() => true)
    );
  }

  /**
   * effettua logout
   */
  logout(): Observable<boolean> {
    return this.apiService.postLogout({ body: {} }).pipe(
      tap(() => this.cleanAuthUser()),
      tap(() => this.requestTokenHeader.setValue(null)),
      map(() => true)
    );
  }

  /**
   * aggiunge azione da eseguire dopo il logon
   */
  addActionAfterLogon(
    action: Observable<void> | (() => void) | (() => Observable<void>)
  ): void {
    if (action instanceof Observable) {
      this.addActionAsObservable(action);
    } else if (typeof action === 'function') {
      this.addActionAsCallback(action);
    }
  }

  /**
   * restituisce gli attributi dell'user autenticato
   */
  getAuthUserAttributes(): UserAttributesDTO {
    return this.getAuthUser().attributes || {};
  }

  /**
   * restituisce la sessione attuale
   */
  getAuthUser(): UserDataDTO {
    const authUser = this.authUser$.getValue();

    return (authUser && { ...authUser }) || {};
  }

  /**
   * restituisce osserbabile per aggiornamenti stato user autenticato
   */
  getAuthUserWhenChange(): Observable<UserDataDTO> {
    return this.authUser$.asObservable();
  }

  /**
   * determina se la sessione è impostata correttamente
   */
  isAuthenticated(): boolean {
    return !!this.isValidUser(this.authUser$.getValue());
  }

  /**
   * stabilisce se l'url è fuori dall'autenticazione
   */
  isUrlOutOfAuth(url: string): boolean {
    const urlOutOfAuth =
      this.appService.getConfig().security.urlOutOfAuth || [];

    return urlOutOfAuth.findIndex(x => url && url.indexOf(x) > -1) > -1;
  }

  /**
   * aggiorna il valore del request token leggendolo dalla response
   */
  updateRequestTokenHeader(res: HttpResponse<void>): void {
    if (this.isRequestTokenHeaderEnabled()) {
      this.requestTokenHeader.setValue(
        res.headers.get(this.requestTokenHeader.name)
      );
    }
  }

  /**
   * verifica la validità dell'auth code
   */
  private authenticate(data: AuthenticateDTO): Observable<boolean> {
    return this.apiService
      .postSecurityLogon({
        body: data,
        options: {
          observe: 'response',
          responseType: 'text'
        }
      })
      .pipe(
        tap((res: HttpResponse<void>) => {
          this.updateRequestTokenHeader(res);
        }),
        map(() => true)
      );
  }

  /**
   * restituisce i dati dell'user autenticato
   */
  private getUserData(): Observable<UserDataDTO> {
    return this.apiService
      .getSecurityUserData({})
      .pipe(map(res => this.sanitizeUserData(res)));
  }

  /**
   * imposta i dati della sessione e controlla che sia valida
   */
  private setAuthUser(authUser: UserDataDTO): void {
    if (!this.isValidUser(authUser)) {
      throw new Error(`User's session is not valid`);
    } else {
      this.authUser$.next(authUser);
    }
  }

  /**
   * rimuove i dati dell'user
   */
  private cleanAuthUser(): void {
    this.authUser$.next(null);
  }

  /**
   * controlla se la sessione è valida
   */
  private isValidUser(user: UserDataDTO): boolean {
    return !!user;
  }

  /**
   * aggiunge azione come osservabile
   */
  private addActionAsObservable(action: Observable<void>): void {
    this.afterLogonActions.push(action);
  }

  /**
   * aggiunge azione funzioen di callback
   */
  private addActionAsCallback(
    action: (() => void) | (() => Observable<void>)
  ): void {
    this.afterLogonActions.push(
      of(true).pipe(
        mergeMap(() => {
          const output = action();

          if (output instanceof Observable) {
            return output;
          } else {
            return of(output);
          }
        })
      )
    );
  }

  /**
   * esegue le operazioni registrate da effettuare dopo il logon
   */
  private runAfterLogonActions(): Observable<boolean> {
    if (this.afterLogonActions.length === 0) {
      return of(true);
    }

    return forkJoin(this.afterLogonActions).pipe(map(() => true));
  }

  /**
   * crea l'oggetto per eseguire l'autenticazione
   */
  private async createAuthenticateData(
    code?: string
  ): Promise<AuthenticateDTO> {
    const sender = this.getAuthSender();
    const testAttr = this.getTestAttr();

    const data: AuthenticateDTO = {
      'application-code': this.getAuthAppCode()
    };

    if (!!sender && !this.appService.getConfig().security.hideSender) {
      data.sender = sender;
    }

    if (!!testAttr) {
      data['test-attribute'] = testAttr;
    }

    if (!!code) {
      data['uuid-info'] = code;
    }

    if (this.deviceService.isNative()) {
      data.deviceToken = this.getDeviceToken();
      data.deviceId = await this.getDeviceId();
      data.platform = await this.getPlatform();
    }

    return data;
  }

  /**
   * restituisce il device token
   */
  private getDeviceToken(): string {
    return this.pushNotificationsService.deviceToken;
  }

  /**
   * restituisce il device id
   */
  private getDeviceId(): Promise<string> {
    return this.deviceService.getDeviceId();
  }

  /**
   * restituisce la piattaforma
   */
  private async getPlatform(): Promise<'G' | 'A'> {
    const deviceInfo = await this.deviceService.getDeviceInfo();

    if (deviceInfo.operatingSystem === 'android') {
      return 'G';
    } else if (deviceInfo.operatingSystem === 'ios') {
      return 'A';
    }
  }

  /**
   * restituisce il sender per eseguire l'autenticazione
   */
  private getAuthSender(): string {
    return this.appService.getAppName('container');
  }

  /**
   * restituisce il codice application per eseguire l'autenticazione
   */
  private getAuthAppCode(): string {
    return this.appService.getEnvironment().appCode.toUpperCase();
  }

  /**
   * restituisce il valore dell'item test attribute
   */
  private getTestAttr(): string {
    return localStorage.getItem('test-attribute');
  }

  /**
   * controlla e sanifica i dati dalla userdata
   */
  private sanitizeUserData(data: UserDataDTO): UserDataDTO {
    // mantengo compatibilità con il parametro deprecato 'position-id'
    if (
      data &&
      data.attributes &&
      typeof data.attributes['position-id'] === 'string' &&
      !data.attributes.positionId
    ) {
      data.attributes.positionId = data.attributes['position-id'].trim();
    }

    return data;
  }

  /**
   * crea l'HttpHeader per gestire le interazione per il qinetic authorization header
   */
  private createRequestTokenHeader(): void {
    const name = this.appService.getConfig().security.authHeaderName;
    const storageKey = this.appService.getConfig().security.authStorageKeyName;

    this.requestTokenHeader = new HttpHeader(name, {
      writeFn: value => {
        if (!value) {
          localStorage.removeItem(storageKey);
        } else {
          localStorage.setItem(storageKey, value);
        }
      },
      readFn: () => {
        return localStorage.getItem(storageKey);
      },
      addAllowedFn: (req: BaseHttpRequest) => {
        return (
          this.isRequestTokenHeaderEnabled() && !this.isUrlOutOfAuth(req.url)
        );
      }
    });

    this.httpReqService.addHeader(this.requestTokenHeader);
  }

  /**
   * stabilisce se l'uso il qinetic authorization header è abilitato
   */
  private isRequestTokenHeaderEnabled(): boolean {
    const demoMode = this.appService.isDemoActive();
    const devMode = this.appService.isDevMode();
    const hostEnv = this.appService.getHostEnv();
    const strategy = this.appService.getConfig().security.authStrategy;

    if (devMode && (hostEnv === 'localhost' || hostEnv === 'develop')) {
      return true;
    } else if (strategy === 'header') {
      return true;
    } else if (strategy === 'cookie') {
      return false;
    } else {
      return !demoMode;
    }
  }
}
